/* ──────────────────────────────────────────────
   src/hooks/factories/useCrudResource.ts
   Factory para crear hooks CRUD genéricos con React Query
   ────────────────────────────────────────────── */
import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import api from '../../api/axios';

export interface CrudResourceConfig<
  TEntity,
  TCreatePayload = Omit<TEntity, 'id'>,
  TUpdatePayload = Partial<TEntity>,
> {
  /** Query key para React Query (ej: ['centros']) */
  queryKey: QueryKey;
  /** Endpoint base de la API (ej: '/centros') */
  endpoint: string;
  /** Tiempo de caché en ms (default: 5 minutos) */
  staleTime?: number;
  /** Función opcional para transformar la respuesta del GET */
  transformResponse?: (data: any) => TEntity[];
  /** Si es true, usa PATCH en lugar de PUT para update */
  usePatchForUpdate?: boolean;
  /** Campos a excluir del payload de create (ej: ['activo']) */
  excludeFromCreate?: (keyof TEntity)[];
}

export interface UseCrudResourceReturn<
  TEntity,
  TCreatePayload = Omit<TEntity, 'id'>,
  TUpdatePayload = Partial<TEntity>,
> {
  /** Datos del recurso */
  rows: TEntity[];
  /** Estado de carga */
  loading: boolean;
  /** Función para refrescar los datos manualmente */
  refresh: () => void;
  /** Función para crear un nuevo recurso */
  create: (data: TCreatePayload) => Promise<any>;
  /** Función para actualizar un recurso existente */
  update: (id: number, data: TUpdatePayload) => Promise<any>;
  /** Función para eliminar un recurso */
  remove: (id: number) => Promise<any>;
  /** Función para activar/desactivar un recurso (toggle) */
  toggle: (id: number, activar: boolean) => Promise<any>;
}

/**
 * Factory para crear hooks CRUD genéricos con React Query
 *
 * @example
 * ```ts
 * export function useCentros() {
 *   return useCrudResource<Centro>({
 *     queryKey: ['centros'],
 *     endpoint: '/centros',
 *   });
 * }
 *
 * export function useClientes() {
 *   return useCrudResource<Cliente, Omit<Cliente, 'id' | 'activo'>>({
 *     queryKey: ['clientes'],
 *     endpoint: '/clientes',
 *     usePatchForUpdate: true,
 *     excludeFromCreate: ['activo'],
 *   });
 * }
 * ```
 */
export function useCrudResource<
  TEntity extends { id: number },
  TCreatePayload = Omit<TEntity, 'id'>,
  TUpdatePayload = Partial<TEntity>,
>(
  config: CrudResourceConfig<TEntity, TCreatePayload, TUpdatePayload>,
): UseCrudResourceReturn<TEntity, TCreatePayload, TUpdatePayload> {
  const {
    queryKey,
    endpoint,
    staleTime = 1000 * 60 * 5,
    transformResponse,
    usePatchForUpdate = false,
    excludeFromCreate = [],
  } = config;
  const qc = useQueryClient();

  /* ---------- 1) Query para listado ---------- */
  const { data: rows = [], isFetching: loading } = useQuery<TEntity[]>({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get<TEntity[]>(endpoint);
      return transformResponse ? transformResponse(data) : data;
    },
    staleTime,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey });
  };

  /* ---------- 2) Mutaciones (React Query) ---------- */
  const createMutation = useMutation({
    mutationFn: (data: TCreatePayload) => {
      // Excluir campos si es necesario
      const payload: any = { ...data };
      excludeFromCreate.forEach((key) => {
        delete payload[key];
      });
      return api.post<TEntity>(endpoint, payload);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TUpdatePayload }) => {
      const method = usePatchForUpdate ? api.patch : api.put;
      return method<TEntity>(`${endpoint}/${id}`, data);
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`${endpoint}/${id}`),
    onSuccess: invalidate,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, activar }: { id: number; activar: boolean }) =>
      api.patch(`${endpoint}/${id}/${activar ? 'activar' : 'desactivar'}`),
    onSuccess: invalidate,
  });

  /* ---------- 3) Wrappers compatibles con API antigua ---------- */
  const create = (data: TCreatePayload) => createMutation.mutateAsync(data);
  const update = (id: number, data: TUpdatePayload) => updateMutation.mutateAsync({ id, data });
  const remove = (id: number) => removeMutation.mutateAsync(id);
  const toggle = (id: number, activar: boolean) => toggleMutation.mutateAsync({ id, activar });

  /* ---------- 4) Refresh (wrapper para compatibilidad) ---------- */
  const refresh = () => invalidate();

  return { rows, loading, refresh, create, update, remove, toggle };
}
